'use client'

import { improveOptionValue } from '@repo/shopify/options'
import { Button } from '@repo/ui/components/button'
import { cn } from '@repo/ui/lib/utils'
import { createHrefForDocument } from '@repo/url/href'
import Link from 'next/link'
import { FunctionComponent, useMemo } from 'react'
import { useProduct, useUpdateURL } from '../../../providers/ProductProvider'
import { SHEET, useSheet } from '../../../providers/SheetProvider'
import { PageDto } from '../../../types/page.types'
import { ProductDto, ShopifyProductDataDto } from '../../../types/product.types'

interface Props {
  pageDto: PageDto
  productDto: ProductDto
  shopifyProductDto: ShopifyProductDataDto
}

const ProductVariantSelector: FunctionComponent<Props> = ({ pageDto, productDto, shopifyProductDto: { shopifyProduct, alternativeShopifyProducts } }) => {
  const { openSheet } = useSheet()
  const updateURL = useUpdateURL()
  const { state, updateOption, getCombinations, extractColorNameFromShopifyProduct } = useProduct()
  const options = useMemo(() => shopifyProduct?.options || [], [shopifyProduct?.options])
  const combinations = useMemo(() => getCombinations(shopifyProduct), [getCombinations, shopifyProduct])
  const hasNoOptionsOrJustOneOption = !options.length || (options.length === 1 && options[0]?.values.length === 1)
  const outOfStockLabel = ` (${pageDto.dictionary?.product?.outofstock || 'Out of stock'})`

  // If we have separate 'color' rendering since these are just alternative Shopify products
  const optionsWithoutColor = useMemo(() => options.filter((option) => !(option.name.toLowerCase() === 'color' && option.values.length === 1)), [options])

  if (hasNoOptionsOrJustOneOption) {
    return null
  }

  return (
    <section className='space-y-3'>
      <h2 className='sr-only'>{pageDto.dictionary?.product?.options || 'Options'}</h2>

      {shopifyProduct && alternativeShopifyProducts.length > 0 && (
        <dl>
          <dt className='sr-only'>{pageDto.dictionary?.product?.color || 'Color'}</dt>
          <dd className='flex flex-wrap items-center justify-end gap-px'>
            {[shopifyProduct, ...alternativeShopifyProducts].map((product) => (
              <Button
                asChild={true}
                key={product.handle}
                title={`${product.title} ${!product.availableForSale ? outOfStockLabel : ''}`}
                variant={product.handle === productDto?.sanityProduct?.urlSlug ? 'outline' : 'secondaryAuto'}
                className={cn(!product.availableForSale && 'opacity-50')}
              >
                <Link href={createHrefForDocument('product', product.handle, pageDto.params.locale)}>{extractColorNameFromShopifyProduct(product)}</Link>
              </Button>
            ))}
          </dd>
        </dl>
      )}

      {optionsWithoutColor.map((option) => (
        <form key={option.id}>
          <dl>
            <dt className='sr-only'>{option.name}</dt>
            <dd className='flex flex-wrap items-center justify-end gap-px'>
              {option.values.map((value) => {
                const optionNameLowerCase = option.name.toLowerCase()

                // Base option params on current selectedOptions so we can preserve any other param state.
                const optionParams = { ...state, [optionNameLowerCase]: value }

                // Filter out invalid options and check if the option combination is available for sale.
                const filtered = Object.entries(optionParams).filter(([key, value]) =>
                  options.find((option) => option.name.toLowerCase() === key && value && option.values.includes(value)),
                )

                const isAvailableForSale = combinations.find((combination) => filtered.every(([key, value]) => combination[key] === value && combination.availableForSale))

                // The option is active if it's in the selected options.
                const isActive = state[optionNameLowerCase] === value

                return (
                  <Button
                    key={value}
                    variant={isActive ? 'outline' : 'secondaryAuto'}
                    title={`${option.name} ${value}${!isAvailableForSale ? outOfStockLabel : ''}`}
                    className={cn(!isAvailableForSale && 'opacity-50')}
                    formAction={() => {
                      const newState = updateOption(optionNameLowerCase, value)

                      updateURL(newState, true)
                    }}
                  >
                    {improveOptionValue(option.name, value, pageDto.shopifyInstance)}
                  </Button>
                )
              })}

              {option.name.toLowerCase() === 'size' && option.values.length > 0 && productDto.sanityProduct?.sizeGuide && (
                <Button variant='secondaryAuto' type='button' onClick={() => openSheet(SHEET.SIZING)}>
                  {pageDto.dictionary?.product?.sizeguide || 'Size Guide'}
                </Button>
              )}
            </dd>
          </dl>
        </form>
      ))}
    </section>
  )
}

export default ProductVariantSelector
